import React, { useState, useEffect, useCallback, useContext } from 'react';
import { useParams } from 'react-router-dom';
import {
    Table, TableHeader, TableBody, TableRow, TableHead, TableCell,
} from 'shared/components/ui/table';
import {
 Button
} from 'shared/components/ui/button';
import {
 Input
} from 'shared/components/ui/input';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from 'shared/components/ui/select';
import {
 Skeleton
} from 'shared/components/ui/skeleton';
import {
    ChevronLeft, ChevronRight, Search, Database, RefreshCw, Loader2,
    Plus, Save, X, Pencil, Trash2,
} from 'lucide-react';
import { bexInsightsService } from 'shared/api/services/BEXO/BeXInsights.service';
import { bexSchemaService } from 'shared/api/services/BEX';
import { Context } from 'shared/context/GlobalState';
import { SchemaListItem } from '../../types/schema.types';

interface SchemaDataTabProps {
    schema: SchemaListItem;
}

interface DataRow {
    recordId: string;
    recordStatus: string;
    [key: string]: any;
}

interface ColumnDef {
    key: string;
    label: string;
}

const PAGE_SIZES = [10, 25, 50];
const SYSTEM_FIELDS = new Set(['recordId', 'recordStatus', 'createdAt', 'updatedAt']);

const SchemaDataTab: React.FC<SchemaDataTabProps> = ({ schema }) => {
    const { appId, oragID } = useParams<{ appId: string; oragID: string }>();
    const { addNewNotifcation } = useContext(Context);

    const [rows, setRows] = useState<DataRow[]>([]);
    const [columns, setColumns] = useState<ColumnDef[]>([]);
    const [totalRecords, setTotalRecords] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [searchTerm, setSearchTerm] = useState('');

    // Add row state
    const [isAddingRow, setIsAddingRow] = useState(false);
    const [newRowData, setNewRowData] = useState<Record<string, string>>({});
    const [isSavingNew, setIsSavingNew] = useState(false);

    // Edit row state
    const [editingRowId, setEditingRowId] = useState<string | null>(null);
    const [editRowData, setEditRowData] = useState<Record<string, string>>({});
    const [isSavingEdit, setIsSavingEdit] = useState(false);

    // Delete row state
    const [deletingRowId, setDeletingRowId] = useState<string | null>(null);

    const totalPages = Math.max(1, Math.ceil(totalRecords / pageSize));

    // Get editable columns (non-system fields from schema)
    const editableColumns = schema.columns
        .filter((c) => !SYSTEM_FIELDS.has(c.columnName))
        .map((c) => c.columnName);

    const fetchData = useCallback(async (page: number, silent = false) => {
        if (!oragID || !appId) return;
        if (!silent) setIsLoading(true);
        setError(null);

        try {
            let response: any;

            if (schema.connectionId) {
                response = await bexSchemaService.searchData({
                    organizationId: oragID,
                    appId,
                    connectionId: schema.connectionId,
                    target: schema.tableName.toLowerCase(),
                    filters: {
                        logicalOperator: 'AND',
                        rules: [
                            {
                                field: 'recordId',
                                comparisonOperator: 'notEquals',
                                value: 'null',
                            },
                        ],
                    },
                    columnsToRetrieve: [],
                    maxRowsPerPage: pageSize,
                    pageToRetrieve: page,
                });
            } else {
                const payload = {
                    target: schema.tableName.toLowerCase(),
                    action: 'search',
                    filters: {
                        logicalOperator: 'AND',
                        rules: [
                            {
                                field: 'recordId',
                                dataType: 'tag',
                                comparisonOperator: 'notEquals',
                                value: 'null',
                            },
                        ],
                    },
                    columnsToRetrieve: [],
                    resultDisplayOrder: 'row',
                    maxRowsPerPage: pageSize,
                    pageToRetrieve: page,
                };
                response = await bexInsightsService.searchV2(oragID, appId, payload);
            }
            const rawRows = response?.tableRow || [];
            const total = response?.totalRecords || 0;

            const columnKeys = Object.keys(
                rawRows.reduce((acc: any, row: any) => ({ ...acc, ...row }), {})
            );

            const schemaFieldNames = new Set(schema.columns.map((c) => c.columnName));
            const orderedKeys = [
                ...columnKeys.filter((k) => schemaFieldNames.has(k)),
                ...columnKeys.filter((k) => !schemaFieldNames.has(k) && !SYSTEM_FIELDS.has(k)),
                ...columnKeys.filter((k) => SYSTEM_FIELDS.has(k)),
            ];

            const uniqueKeys = Array.from(new Set(orderedKeys));

            setColumns(uniqueKeys.map((key) => ({
 key,
label: key
})));
            setRows(rawRows);
            setTotalRecords(total);
        } catch (err: any) {
            console.error('Error fetching data:', err);
            setError(err?.message || 'Failed to fetch data');
            setRows([]);
            setTotalRecords(0);
        } finally {
            setIsLoading(false);
        }
    }, [oragID, appId, schema.tableName, schema.columns, schema.connectionId, pageSize]);

    useEffect(() => {
        fetchData(currentPage);
    }, [fetchData, currentPage]);

    useEffect(() => {
        setCurrentPage(1);
    }, [schema.tableName]);

    const filteredRows = searchTerm
        ? rows.filter((row) =>
            Object.values(row).some((val) =>
                String(val).toLowerCase().includes(searchTerm.toLowerCase())
            )
        )
        : rows;

    const formatCellValue = (value: any): string => {
        if (value === null || value === undefined) return '-';
        if (typeof value === 'object') return JSON.stringify(value);
        return String(value);
    };

    // ─── Add Row ──────────────────────────────────────────────────

    const handleStartAddRow = () => {
        const initial: Record<string, string> = {};
        editableColumns.forEach((col) => { initial[col] = ''; });
        setNewRowData(initial);
        setIsAddingRow(true);
    };

    const handleCancelAddRow = () => {
        setIsAddingRow(false);
        setNewRowData({});
    };

    const handleSaveNewRow = async () => {
        if (!oragID || !appId) return;
        setIsSavingNew(true);
        try {
            await bexSchemaService.createDataRecord({
                organizationId: oragID,
                appId,
                tableName: schema.tableName.toLowerCase(),
                record: newRowData,
            });
            await fetchData(currentPage, true);
            addNewNotifcation('Record added successfully', 'success');
            setIsAddingRow(false);
            setNewRowData({});
        } catch (err: any) {
            console.error('Error adding record:', err);
            addNewNotifcation(err?.message || 'Failed to add record', 'danger');
        } finally {
            setIsSavingNew(false);
        }
    };

    // ─── Edit Row ─────────────────────────────────────────────────

    const handleStartEdit = (row: DataRow) => {
        const data: Record<string, string> = {};
        editableColumns.forEach((col) => {
            data[col] = row[col] !== undefined && row[col] !== null ? String(row[col]) : '';
        });
        setEditRowData(data);
        setEditingRowId(row.recordId);
    };

    const handleCancelEdit = () => {
        setEditingRowId(null);
        setEditRowData({});
    };

    const handleSaveEdit = async () => {
        if (!oragID || !appId || !editingRowId) return;
        setIsSavingEdit(true);
        try {
            await bexSchemaService.updateDataRecord({
                organizationId: oragID,
                appId,
                tableName: schema.tableName.toLowerCase(),
                recordId: editingRowId,
                updates: editRowData,
            });
            await fetchData(currentPage, true);
            addNewNotifcation('Record updated successfully', 'success');
            setEditingRowId(null);
            setEditRowData({});
        } catch (err: any) {
            console.error('Error updating record:', err);
            addNewNotifcation(err?.message || 'Failed to update record', 'danger');
        } finally {
            setIsSavingEdit(false);
        }
    };

    // ─── Delete Row ────────────────────────────────────────────────

    const handleDeleteRow = async (recordId: string) => {
        if (!oragID || !appId) return;
        setDeletingRowId(recordId);
        try {
            await bexSchemaService.deleteDataRecord({
                organizationId: oragID,
                appId,
                tableName: schema.tableName.toLowerCase(),
                recordId,
            });
            await fetchData(currentPage, true);
            addNewNotifcation('Record deleted successfully', 'success');
        } catch (err: any) {
            console.error('Error deleting record:', err);
            addNewNotifcation(err?.message || 'Failed to delete record', 'danger');
        } finally {
            setDeletingRowId(null);
        }
    };

    // ─── Render ───────────────────────────────────────────────────

    if (error) {
        return (
            <div className="flex flex-col items-center justify-center py-16 gap-4">
                <Database className="h-10 w-10 text-muted-foreground" />
                <p className="text-sm text-muted-foreground">{error}</p>
                <Button variant="outline" size="sm" onClick={() => fetchData(currentPage)}>
                    <RefreshCw className="mr-2 h-3.5 w-3.5" />
                    Retry
                </Button>
            </div>
        );
    }

    // Determine display columns: use fetched columns if available, else all schema columns
    const displayColumns = columns.length > 0
        ? columns
        : schema.columns.map((c) => ({ key: c.columnName, label: c.columnName }));

    return (
        <div className="flex flex-col gap-3">
            {/* Toolbar */}
            <div className="flex items-center justify-between gap-3">
                <div className="relative flex-grow max-w-sm">
                    <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                    <Input
                        placeholder="Filter rows..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="pl-8 h-8 text-sm"
                    />
                </div>
                <div className="flex items-center gap-2">
                    <span className="text-xs text-muted-foreground">
                        {totalRecords} record{totalRecords !== 1 ? 's' : ''}
                    </span>
                    <Button
                        variant="outline"
                        size="sm"
                        className="h-7 text-xs"
                        onClick={handleStartAddRow}
                        disabled={isAddingRow || schema.columns.length === 0}
                    >
                        <Plus className="mr-1 h-3.5 w-3.5" />
                        Add Row
                    </Button>
                    <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7"
                        onClick={() => fetchData(currentPage)}
                        disabled={isLoading}
                    >
                        {isLoading ? (
                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        ) : (
                            <RefreshCw className="h-3.5 w-3.5" />
                        )}
                    </Button>
                </div>
            </div>

            {/* Data Table */}
            {isLoading && rows.length === 0 && !isSavingNew && !isSavingEdit ? (
                <div className="rounded-md border">
                    <Table>
                        <TableHeader>
                            <TableRow>
                                {Array.from({
 length: 5
}).map((_, i) => (
    <TableHead key={i}><Skeleton className="h-4 w-20" /></TableHead>
                                ))}
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {Array.from({
 length: 5
}).map((_, i) => (
    <TableRow key={i}>
        {Array.from({
 length: 5
}).map((_, j) => (
    <TableCell key={j}><Skeleton className="h-4 w-24" /></TableCell>
                                    ))}
    </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </div>
            ) : (
                <div className="rounded-md border overflow-auto max-h-[60vh]">
                    <Table>
                        <TableHeader className="sticky top-0 bg-background z-10">
                            <TableRow>
                                <TableHead className="w-[70px] text-xs">Actions</TableHead>
                                {displayColumns.map((col) => (
                                    <TableHead key={col.key} className="whitespace-nowrap text-xs">
                                        {col.label}
                                    </TableHead>
                                ))}
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {/* Add new row */}
                            {isAddingRow && (
                                <TableRow className="bg-muted/40">
                                    <TableCell className="text-xs">
                                        <div className="flex items-center gap-1">
                                            <Button
                                                variant="ghost"
                                                size="icon"
                                                className="h-6 w-6"
                                                onClick={handleSaveNewRow}
                                                disabled={isSavingNew}
                                            >
                                                {isSavingNew ? (
                                                    <Loader2 className="h-3 w-3 animate-spin" />
                                                ) : (
                                                    <Save className="h-3 w-3 text-green-600" />
                                                )}
                                            </Button>
                                            <Button
                                                variant="ghost"
                                                size="icon"
                                                className="h-6 w-6"
                                                onClick={handleCancelAddRow}
                                                disabled={isSavingNew}
                                            >
                                                <X className="h-3 w-3 text-destructive" />
                                            </Button>
                                        </div>
                                    </TableCell>
                                    {displayColumns.map((col) => (
                                        <TableCell key={col.key} className="text-xs p-1">
                                            {SYSTEM_FIELDS.has(col.key) ? (
                                                <span className="text-muted-foreground">auto</span>
                                            ) : (
                                                <Input
                                                    value={newRowData[col.key] || ''}
                                                    onChange={(e) =>
                                                        setNewRowData((prev) => ({ ...prev, [col.key]: e.target.value }))
                                                    }
                                                    className="h-7 text-xs"
                                                    placeholder={col.label}
                                                />
                                            )}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            )}

                            {/* Data rows */}
                            {filteredRows.length === 0 && !isAddingRow ? (
                                <TableRow>
                                    <TableCell colSpan={displayColumns.length + 1} className="text-center py-12">
                                        <div className="flex flex-col items-center gap-3">
                                            <Database className="h-8 w-8 text-muted-foreground" />
                                            <p className="text-sm text-muted-foreground">
                                                {rows.length === 0 ? 'No data in this table' : 'No matching rows'}
                                            </p>
                                            {rows.length === 0 && schema.columns.length > 0 && (
                                                <Button
                                                    variant="outline"
                                                    size="sm"
                                                    className="text-xs"
                                                    onClick={handleStartAddRow}
                                                >
                                                    <Plus className="mr-1 h-3.5 w-3.5" />
                                                    Add first row
                                                </Button>
                                            )}
                                        </div>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                filteredRows.map((row, idx) => {
                                    const isEditing = editingRowId === row.recordId;
                                    return (
                                        <TableRow key={row.recordId || idx}>
                                            <TableCell className="text-xs">
                                                {isEditing ? (
                                                    <div className="flex items-center gap-1">
                                                        <Button
                                                            variant="ghost"
                                                            size="icon"
                                                            className="h-6 w-6"
                                                            onClick={handleSaveEdit}
                                                            disabled={isSavingEdit}
                                                        >
                                                            {isSavingEdit ? (
                                                                <Loader2 className="h-3 w-3 animate-spin" />
                                                            ) : (
                                                                <Save className="h-3 w-3 text-green-600" />
                                                            )}
                                                        </Button>
                                                        <Button
                                                            variant="ghost"
                                                            size="icon"
                                                            className="h-6 w-6"
                                                            onClick={handleCancelEdit}
                                                            disabled={isSavingEdit}
                                                        >
                                                            <X className="h-3 w-3 text-destructive" />
                                                        </Button>
                                                    </div>
                                                ) : (
                                                    <div className="flex items-center gap-1">
                                                        <Button
                                                            variant="ghost"
                                                            size="icon"
                                                            className="h-6 w-6"
                                                            onClick={() => handleStartEdit(row)}
                                                            disabled={!!editingRowId || isAddingRow || !!deletingRowId}
                                                        >
                                                            <Pencil className="h-3 w-3" />
                                                        </Button>
                                                        <Button
                                                            variant="ghost"
                                                            size="icon"
                                                            className="h-6 w-6"
                                                            onClick={() => handleDeleteRow(row.recordId)}
                                                            disabled={!!editingRowId || isAddingRow || !!deletingRowId}
                                                        >
                                                            {deletingRowId === row.recordId ? (
                                                                <Loader2 className="h-3 w-3 animate-spin" />
                                                            ) : (
                                                                <Trash2 className="h-3 w-3 text-destructive" />
                                                            )}
                                                        </Button>
                                                    </div>
                                                )}
                                            </TableCell>
                                            {displayColumns.map((col) => (
                                                <TableCell key={col.key} className="text-xs whitespace-nowrap max-w-[200px]">
                                                    {isEditing && !SYSTEM_FIELDS.has(col.key) ? (
                                                        <Input
                                                            value={editRowData[col.key] || ''}
                                                            onChange={(e) =>
                                                                setEditRowData((prev) => ({ ...prev, [col.key]: e.target.value }))
                                                            }
                                                            className="h-7 text-xs"
                                                        />
                                                    ) : (
                                                        <span className="truncate block">
                                                            {formatCellValue(row[col.key])}
                                                        </span>
                                                    )}
                                                </TableCell>
                                            ))}
                                        </TableRow>
                                    );
                                })
                            )}
                        </TableBody>
                    </Table>
                </div>
            )}

            {/* Pagination */}
            {totalRecords > 0 && (
                <div className="flex items-center justify-between text-xs text-muted-foreground">
                    <div className="flex items-center gap-2">
                        <span>Rows per page:</span>
                        <Select
                            value={String(pageSize)}
                            onValueChange={(v) => { setPageSize(Number(v)); setCurrentPage(1); }}
                        >
                            <SelectTrigger className="h-7 w-16 text-xs">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                {PAGE_SIZES.map((size) => (
                                    <SelectItem key={size} value={String(size)}>{size}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    <div className="flex items-center gap-2">
                        <span>
                            Page {currentPage} of {totalPages}
                        </span>
                        <Button
                            variant="outline"
                            size="icon"
                            className="h-7 w-7"
                            disabled={currentPage <= 1}
                            onClick={() => setCurrentPage((p) => p - 1)}
                        >
                            <ChevronLeft className="h-3.5 w-3.5" />
                        </Button>
                        <Button
                            variant="outline"
                            size="icon"
                            className="h-7 w-7"
                            disabled={currentPage >= totalPages}
                            onClick={() => setCurrentPage((p) => p + 1)}
                        >
                            <ChevronRight className="h-3.5 w-3.5" />
                        </Button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default SchemaDataTab;
